import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HTTPCONSTANTS } from '../http/http-constants';
import { PlateViewerConfig } from '../models/plateViewerConfig';
import { PlatexComparisonSite } from '../models/platexComparisonSite';
import { SplitSearchResp } from '../models/splitSearchResp';
import { PlatopediaWord } from '../models/platopediaWord';
import { RawSoldPlate, Registration } from '../models/registration';
import { AdminWordbankWord } from '../pages/admin-wordbank/admin-wordbank.component';
import { BlogCreateRequest } from '../models/blogCreateRequest';
import { ComparableCollectionResponse } from '../models/comparableEvidence';
import {
  AdminUserDetail,
  UsersOverviewQuery,
  UsersOverviewResponse,
} from '../models/adminUsers';

// A member-submitted plate photo awaiting editor review.
export interface PendingPlatePhoto {
  id: number;
  registration: string;
  imageUrl: string;
  make: string;
  model: string;
  colour: string;
  yearSeen: string;
  source: string;
  sourceUrl: string;
  createdAt: string;
}
import { DealerHealthResponse } from '../models/dealerHealth';

@Injectable({ providedIn: 'root' })
export class AdminService {
  constructor(private http: HttpClient) { }

  public verify(): Observable<any> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/authenticate/verify`;
    return this.http.get(url);
  }

  // Read-only Admin Users overview (paginated/searchable/sortable).
  public getUsersOverview(
    query: UsersOverviewQuery
  ): Observable<UsersOverviewResponse> {
    const params = new URLSearchParams();
    params.set('page', String(query.page));
    params.set('pageSize', String(query.pageSize));
    if (query.search) params.set('search', query.search);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDir) params.set('sortDir', query.sortDir);
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/users-overview?${params.toString()}`;
    return this.http.get<UsersOverviewResponse>(url);
  }

  // Read-only per-user detail (saved searches, notify watches, favourites).
  public getUserDetail(userId: string): Observable<AdminUserDetail> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/users/${userId}/detail`;
    return this.http.get<AdminUserDetail>(url);
  }

  // Read-only Dealer Health dashboard (stored compare evidence per dealer).
  // includeCoverage triggers the expensive sole/shared snapshot (cached
  // server-side with an asOf timestamp); never part of the default load.
  public getDealerHealth(
    refresh: boolean,
    includeCoverage: boolean = false
  ): Observable<DealerHealthResponse> {
    const params = new URLSearchParams();
    if (refresh) params.set('refresh', 'true');
    if (includeCoverage) params.set('includeCoverage', 'true');
    const query = params.toString();
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/dealer-health${
      query ? '?' + query : ''
    }`;
    return this.http.get<DealerHealthResponse>(url);
  }

  public BYOSoldPlates(style, prefix, numbers, letters, callback: (res: RawSoldPlate[]) => void): void {
    this.http
      .get<RawSoldPlate[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=controller-admin-byo&style=${style}&prefix=${prefix}&numbers=${numbers}&letters=${letters}`
      )
      .subscribe(
        (_: RawSoldPlate[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
 };

  public MultipleSales(style, callback: (res: RawSoldPlate[]) => void): void {
    this.http
      .get<RawSoldPlate[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=controller-admin-multiple_verified&style=${style}`
      )
      .subscribe(
        (_: RawSoldPlate[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  };

  public YearlySales(style: string, year: string, callback: (res: RawSoldPlate[]) => void): void {
    this.http
      .get<RawSoldPlate[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=controller-admin-top-yearly-sold&year=${year}&style=${style}`
      )
      .subscribe(
        (_: RawSoldPlate[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  };

  /** Comparable EVIDENCE collection (no valuation) — shared by the admin
   * Comparables tab and the blog editor. Uses the canonical plato-search
   * placeholders so the dev2 Mode 2 sed-rewrite never diverts it. */
  public GetComparables(
    plate: string,
    term: string,
    limit: number,
    minScore: number,
    band: number
  ): Observable<ComparableCollectionResponse> {
    const params = [
      'process=admin-comparables',
      `plate=${encodeURIComponent(plate)}`,
    ];
    if (term) params.push(`term=${encodeURIComponent(term)}`);
    if (limit) params.push(`limit=${limit}`);
    if (minScore || minScore === 0) params.push(`min_score=${minScore}`);
    if (band || band === 0) params.push(`band=${band}`);
    return this.http.get<ComparableCollectionResponse>(
      `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}?${params.join('&')}`
    );
  }

  public getMetricsForPeriod(trackingType: number, from: Date, to: Date, callback: (res: { registration: string, track_type: number, result_count: number }[]) => void): void {
    this.http
      .get<{ registration: string, track_type: number, result_count: number }[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=admin-metrics&tracking_type=${trackingType}&date_from=${from.toISOString().substr(0, 10)}&date_to=${to.toISOString().substr(0, 10)}`
      )
      .subscribe(
        (_: { registration: string, track_type: number, result_count: number }[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  }

  public GetRecentlyAdded(callback: (res: Registration[]) => void): void {
    this.http
      .get<Registration[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=admin-weekly-third-party-additions`
      )
      .subscribe(
        (_: Registration[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  }

  // Get the list of images
  getImages(): Observable<string[]> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/get-images`;
    return this.http.get<string[]>(url);
  }

  // Upload a static image
  uploadStaticImage(file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const headers = new HttpHeaders();
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/add-static-media/${file.name}`;
    return this.http.post<{ url: string }>(url, formData, { headers });
  }

  // --- Member plate-photo moderation queue (Plate Image Researcher) ---
  // Pending member submissions awaiting an editor's approve/reject. Backed by
  // admin/plate-photos/{pending,{id}/approve,{id}/reject} (IsAdmin-guarded).
  public getPendingPlatePhotos(): Observable<PendingPlatePhoto[]> {
    return this.http.get<PendingPlatePhoto[]>(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/plate-photos/pending`
    );
  }

  public approvePlatePhoto(id: number): Observable<any> {
    return this.http.post(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/plate-photos/${id}/approve`,
      {}
    );
  }

  public rejectPlatePhoto(id: number, notes: string = ''): Observable<any> {
    return this.http.post(
      `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/plate-photos/${id}/reject`,
      { notes }
    );
  }

  public UpdateBlog(id: number, blogRequest: BlogCreateRequest, callback: (status: boolean) => void): void {
    this.http.put(`${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/blog/update-blog/${id}`, blogRequest).subscribe(
      (response) => {
        console.log('Blog updated successfully:', response);
        callback(true);
      },
      (error) => {
        console.error('Error creating blog:', error);
        callback(false);
      }
    );
  }

  public CreateBlog(blogRequest: BlogCreateRequest, callback: (status: boolean) => void): void {
    this.http.post(`${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/blog/create-blog`, blogRequest).subscribe(
      (response) => {
        console.log('Blog created successfully:', response);
        callback(true);
      },
      (error) => {
        console.error('Error creating blog:', error);
        callback(false);
      }
    );
  }

  public getWordbank(filter: string, callback: (res: AdminWordbankWord[]) => void): void {
    this.http
      .get<AdminWordbankWord[]>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=admin-term-list&filters=${filter}`
      )
      .subscribe(
        (_: AdminWordbankWord[]) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  }

  public DeleteWordbank(term: number, callback: () => void): void {
    this.http
      .get(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=admin-delete-term&term=${term}`
      )
      .subscribe(
        () => {
          callback();
        },
        () => {
          callback();
        }
      );
  }

  public searchAI(
    search: string,
    callback: (res: { request: string, query: string, res: RawSoldPlate[] }) => void
  ): void {
    this.http
      .get<{ request: string, query: string, res: RawSoldPlate[] }>(
        `${HTTPCONSTANTS.plato_search_protocol}${HTTPCONSTANTS.plato_search_base}?process=data-ai-helper&term=${search}`
      )
      .subscribe(
        (_: { request: string, query: string, res: RawSoldPlate[] }) => {
          callback(_);
        },
        () => {
          callback(null);
        }
      );
  }

  public splitSearch(
    term: string,
    plate1: string,
    plate2: string
  ): Observable<SplitSearchResp> {
    var url = `${HTTPCONSTANTS.platoProtocol}${HTTPCONSTANTS.platoBaseAddress}/side-compare/${term}/${plate1}/${plate2}`;
    return this.http.get<SplitSearchResp>(url);
  }

  public getAdminImageConfigGroups(): Observable<PlateViewerConfig[]> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/admin-get-plate-viewer-config-groups`;
    return this.http.get<PlateViewerConfig[]>(url);
  }

  public getImageConfigGroups(): Observable<PlateViewerConfig[]> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/get-plate-viewer-config-groups`;
    return this.http.get<PlateViewerConfig[]>(url);
  }

  public getImageConfigsForGroup(
    groupId: string
  ): Observable<PlateViewerConfig[]> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/admin/get-plate-viewer-config-by-group/${groupId}`;
    return this.http.get<PlateViewerConfig[]>(url);
  }

  public getCompareSites(): Observable<PlatexComparisonSite[]> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/compare/sites`;
    return this.http.get<PlatexComparisonSite[]>(url);
  }

  public addPlatopediaWord(word: PlatopediaWord): Observable<void> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/platopedia/add`;
    return this.http.post<void>(url, word);
  }

  public addMultiTermHeader(header: string): Observable<void> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/multi-term/header/add/${header}`;
    return this.http.get<void>(url);
  }

  public addMultiTermsToHeader(
    header: string,
    terms: string[]
  ): Observable<void> {
    var url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/search/multi-term/terms/add/${header}`;
    return this.http.post<void>(url, terms);
  }
}
